import {
  useRef,
  useState,
  useEffect,
  createContext,
  type ElementType,
  type ReactNode,
  type KeyboardEventHandler,
  type ChangeEventHandler,
} from 'react'
import { nanoid } from 'nanoid'
import { css } from '@emotion/react'
import { SpacingToken } from '@livechat/design-system-react-components'

import { JSONParse } from '@/utils/func'
import { Keys } from '@/constants'

const toggleCss = css`
  .toggle__input {
    display: none;
  }

  .toggle__content {
    display: none;
    overflow: hidden;
  }

  .toggle__label {
    cursor: pointer;
    display: flex;
    align-items: center;
    padding-block: var(${SpacingToken.Spacing2});

    &--open {
      display: flex;
    }

    &--close {
      display: none;
    }
  }

  .toggle__label-wrapper:focus > .toggle__label {
    outline: -webkit-focus-ring-color auto 5px;
  }

  .toggle__label-wrapper:focus,
  .toggle__label:focus {
    outline: none;
  }

  .toggle__input:checked + .toggle__label-wrapper + .toggle__content {
    display: block;
  }

  .toggle__input:checked + .toggle__label-wrapper .toggle__label--open {
    display: none;
  }
  .toggle__input:checked + .toggle__label-wrapper .toggle__label--close {
    display: flex;
  }
`

interface ToggleContextType {
  isOpen: boolean
  toggle: () => void
}

const ToggleContext = createContext<ToggleContextType | undefined>(undefined)

interface Props {
  tag?: ElementType
  label?: ReactNode
  openLabel?: ReactNode
  closeLabel?: ReactNode
  content?: ReactNode
  storageKey?: string
}

const Toggle = ({
  tag: Tag = 'div',
  label,
  openLabel,
  closeLabel,
  content,
  storageKey,
  ...rest
}: Props) => {
  const getValueFromSessionStorage = (key: string) => {
    if (typeof window !== 'undefined') {
      const value = window.sessionStorage.getItem(key) || ''
      return JSONParse<boolean>(value) || false
    }

    return false
  }

  let toggleTimeout: NodeJS.Timeout
  const inputRef = useRef<HTMLInputElement>(null)
  const initialValue = !!storageKey && getValueFromSessionStorage(storageKey)
  const [isOpen, setIsOpen] = useState<boolean>(initialValue || false)

  const toggleContent = () => {
    if (inputRef.current) {
      inputRef.current.click()
      setIsOpen(!isOpen)
    }
  }

  const saveToStorage: ChangeEventHandler<HTMLInputElement> = (e) => {
    setIsOpen(!isOpen)
    if (storageKey && typeof window !== 'undefined') {
      toggleTimeout = setTimeout(() => {
        return window.sessionStorage.setItem(
          storageKey,
          e.target.checked.toString()
        )
      })
    }
  }

  const handleKeyDown: KeyboardEventHandler<HTMLLabelElement> = (e) => {
    switch (e.key) {
      case Keys.Space:
      case Keys.Enter:
        e.preventDefault()
        toggleContent()
        break

      default:
    }
  }

  useEffect(() => clearTimeout(toggleTimeout))

  const inputKey = storageKey || 'toggle-' + nanoid()

  return (
    <ToggleContext.Provider
      value={{
        isOpen,
        toggle: toggleContent,
      }}
    >
      <Tag className="toggle" css={toggleCss} {...rest}>
        <input
          id={inputKey}
          className="toggle__input"
          type="checkbox"
          onChange={saveToStorage}
          defaultChecked={initialValue}
          ref={inputRef}
        />

        <label
          onKeyDown={handleKeyDown}
          htmlFor={inputKey}
          className="toggle__label-wrapper"
          tabIndex={0}
        >
          <div className="toggle__label" tabIndex={-1}>
            {openLabel && (
              <div className="toggle__label--open">{openLabel}</div>
            )}
            {closeLabel && (
              <div className="toggle__label--close">{closeLabel}</div>
            )}
            {label}
          </div>
        </label>

        <div className="toggle__content">{content}</div>
      </Tag>
    </ToggleContext.Provider>
  )
}

export { ToggleContext, Toggle }
